import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { SubscriptionUpdateInput } from '../generated/globalTypes';
import { updateSubscriptionMutation, updateSubscriptionMutationVariables } from '../generated/updateSubscriptionMutation';
import { subscriptionFieldsFragment } from '../models/Subscription';

/**
 * Get a callback to update a Subscription in the store.
 */
export function useUpdateSubscriptionCallback(): [(id: string, model: SubscriptionUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateSubscriptionMutation, updateSubscriptionMutationVariables>(
        gql`
            mutation updateSubscriptionMutation ($model: SubscriptionUpdateInput!) {
                updateSubscription(model: $model) {
                    ...subscriptionFields
                }
            }

            ${subscriptionFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: SubscriptionUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
